import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";

import { fetchVideos } from "@/lib/queries";
import { TOPIC_LABEL, TOPIC_TAGLINE, TOPICS, type Topic } from "@/lib/topics";
import { useTopicPreferences } from "@/hooks/use-topic-preferences";

import { ImageCardGrid, type ImageCardItem } from "@/components/ImageCardGrid";
import { SeoNote } from "@/components/SeoNote";
import { PageTuneSlot } from "@/components/TunePanel";

const PAGE_SIZE = 12;

type TabKey = "alle" | Topic;

const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: "alle", label: "Alle" },
  ...TOPICS.map((t) => ({ key: t as TabKey, label: TOPIC_LABEL[t] })),
];

export const Route = createFileRoute("/video")({
  head: () => ({
    meta: [
      { title: "Video – Flâneriet" },
      {
        name: "description",
        content:
          "Kuraterede YouTube- og Vimeo-videoer indenfor vin, kunst, jazz, poesi, kultur, mad, cykling, levekunst og have – udvalgt til den langsomme opmærksomhed.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Flâneriet" },
      { property: "og:title", content: "Video – Flâneriet" },
      {
        property: "og:description",
        content:
          "Kuraterede videoer: portrætter, dokumentarer, koncerter og oplæsninger – samlet ét sted.",
      },
      { property: "og:url", content: "https://flaneriet.online/video" },
      { property: "og:image", content: "https://flaneriet.online/og/og-video.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Flâneriet – Video" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Video – Flâneriet" },
      { name: "twitter:description", content: "Kuraterede videoer: portrætter, dokumentarer, koncerter og oplæsninger – samlet ét sted." },
      { name: "twitter:image", content: "https://flaneriet.online/og/og-video.png" },
    ],
    links: [{ rel: "canonical", href: "https://flaneriet.online/video" }],
  }),
  component: VideoPage,
});

function VideoPage() {
  const [tab, setTab] = useState<TabKey>("alle");
  const [query, setQuery] = useState("");
  const { isEnabled, toggle, enableAll, disableAll, enabled } = useTopicPreferences();
  const [visible, setVisible] = useState(PAGE_SIZE);

  const TABS = useMemo(
    () => ALL_TABS.filter((t) => t.key === "alle" || isEnabled(t.key as Topic)),
    [isEnabled],
  );

  useEffect(() => {
    if (tab !== "alle" && !isEnabled(tab as Topic)) setTab("alle");
  }, [tab, isEnabled]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [tab, query]);

  const videos = useQuery({ queryKey: ["videos"], queryFn: () => fetchVideos() });

  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    let list = videos.data ?? [];
    if (tab === "alle") list = list.filter((v) => enabled.has(v.topic));
    else list = list.filter((v) => v.topic === tab);
    if (!q) return list;
    return list.filter((v) => {
      const hay = [v.title, v.description, v.channel, TOPIC_LABEL[v.topic as Topic]]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [videos.data, tab, q, enabled]);

  return (
    <div>
      <PageTuneSlot>
        <section>
          <p className="eyebrow mb-3">Søg</p>
          <div className="relative">
            <Search size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
            <label className="sr-only" htmlFor="tune-video-search">Søg i videoer</label>
            <input
              id="tune-video-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hvad leder du efter?"
              className="w-full bg-transparent border-0 border-b border-rule outline-none focus:ring-0 focus:border-bordeaux py-2 pl-7 pr-2 font-sans text-[14px] text-ink placeholder:text-ink-soft transition-colors"
            />
          </div>
        </section>

        <section>
          <p className="eyebrow mb-3">Emne</p>
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  aria-pressed={active}
                  className={`eyebrow px-3 py-1.5 rounded-full border transition-colors ${
                    active
                      ? "border-bordeaux text-bordeaux bg-bordeaux/5"
                      : "border-rule text-ink-soft hover:text-bordeaux hover:border-bordeaux"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
            <p className="eyebrow">Interesseområder</p>
            <div className="flex items-center gap-3">
              <button onClick={enableAll} className="meta italic hover:text-bordeaux transition-colors">Vælg alle</button>
              <button onClick={disableAll} className="meta italic hover:text-bordeaux transition-colors">Fravælg alle</button>
            </div>
          </div>
          <p className="meta italic mb-3">Slå fra det, du ikke vil se. Gemmes lokalt.</p>
          <div className="flex flex-wrap gap-2">
            {TOPICS.map((t) => {
              const on = isEnabled(t);
              return (
                <button
                  key={t}
                  onClick={() => toggle(t)}
                  aria-pressed={on}
                  className={`eyebrow px-3 py-1.5 rounded-full border transition-colors ${
                    on
                      ? "border-bordeaux text-bordeaux bg-bordeaux/5"
                      : "border-rule text-ink-soft hover:text-bordeaux hover:border-bordeaux"
                  }`}
                >
                  {TOPIC_LABEL[t]}
                </button>
              );
            })}
          </div>
        </section>
      </PageTuneSlot>


      <header className="pt-12 pb-6 border-b-[3px] border-b-double border-b-ink">
        <p className="eyebrow mb-2.5">Se & lyt · til en langsom aften</p>
        <h1 className="font-serif font-normal text-[2.4rem] md:text-[3rem] leading-[1.02] text-ink m-0">
          {tab === "alle" ? "Video" : TOPIC_LABEL[tab as Topic]}
        </h1>
        <p className="mt-3 meta italic max-w-xl">
          {q
            ? <>Søgning efter «{query}» · {filtered.length} {filtered.length === 1 ? "resultat" : "resultater"}</>
            : tab === "alle"
            ? "Portrætter, dokumentarer, koncerter og oplæsninger – udvalgt til den langsomme opmærksomhed."
            : TOPIC_TAGLINE[tab as Topic]}
        </p>
      </header>


      <section className="min-w-0">
        {videos.isLoading && <p className="meta italic py-16 text-center">Henter …</p>}

        {!videos.isLoading && filtered.length === 0 && (
          <p className="meta italic py-16 text-center">
            Endnu ingen videoer her. Kom tilbage om lidt – redaktionen samler løbende.
          </p>
        )}

        {filtered.length > 0 && (
          <ImageCardGrid
            saveKind="video"
            items={filtered.slice(0, visible).map((v): ImageCardItem => {
              const secs = v.duration_seconds ?? 0;
              let dur: string | null = null;
              let durBadge: string | null = null;
              if (secs > 0) {
                const totalM = Math.floor(secs / 60);
                const sec = secs % 60;
                if (totalM >= 60) {
                  const h = Math.floor(totalM / 60);
                  dur = `${h} t ${totalM % 60} min`;
                  durBadge = `${h}:${String(totalM % 60).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
                } else {
                  dur = `${totalM} min`;
                  durBadge = `${totalM}:${String(sec).padStart(2, "0")}`;
                }
              }
              const provider = v.provider === "youtube" ? "YouTube" : "Vimeo";
              return {
                id: v.id,
                kicker: (
                  <>
                    {TOPIC_LABEL[v.topic as Topic]}
                    <span className="mx-1.5 opacity-40">·</span>
                    {provider}
                  </>
                ),
                title: v.title,
                description: v.description || undefined,
                meta: (
                  <>
                    {v.channel ?? provider}
                    {dur ? <span className="text-accent"> · {dur}</span> : null}
                  </>
                ),
                imageUrl: v.thumbnail_url,
                category: v.topic,
                ratio: "16/9",
                play: true,
                duration: durBadge,
                href: v.url,
              };

            })}
          />
        )}
      </section>



      {filtered.length > visible && (
        <div className="pb-6 text-center">
          <button
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className="eyebrow text-bordeaux border-b border-bordeaux pb-0.5 hover:opacity-70 transition-opacity"
          >
            Vis flere ({filtered.length - visible} tilbage)
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <p className="meta italic pb-10 text-center">
          Viser {Math.min(visible, filtered.length)}{" "}af {filtered.length}{" "}
          {filtered.length === 1 ? "video" : "videoer"}
        </p>
      )}

      <SeoNote eyebrow="Om videoerne">
        <p>
          Video er Flâneriets bevægelige læsesal – kuraterede YouTube- og Vimeo-optagelser inden for
          vin, kunst, jazz, klassisk musik, poesi, litteratur, mad, cykling, levekunst og have.
          Portrætter, dokumentarer, koncerter, oplæsninger og samtaler – udvalgt til den, der har
          lyst til at se noget helt igennem.
        </p>
        <p>
          Alle videoer afspilles direkte på siden. Ingen forslag der trækker dig videre, ingen
          autoplay til det næste. Én ting ad gangen.
        </p>
      </SeoNote>
    </div>
  );
}
